import axios from 'axios';
import { API_URL } from '../constantes/constantes';
import { withTokenRetry } from './apiWrapper';

/**
 * Servicio para realizar búsquedas de contenido con filtros y paginación
 * 
 * @description Ejecuta búsquedas en el sistema de contenido de UnoTV/Claro Sports
 * con soporte para filtrado por categoría, paginación y autenticación por token.
 * Maneja automáticamente la codificación de parámetros y diferentes códigos de respuesta.
 * 
 * @async
 * @function getSearch
 * 
 * @param {string} query - Término de búsqueda (se codifica automáticamente)
 * @param {number} page - Número de página (0-indexed)
 * @param {number} PAGESIZE - Cantidad de resultados por página
 * @param {string} category - Categoría para filtrar (puede ser string vacío)
 * @param {string} token - Token de autenticación Bearer
 * 
 * @returns {Promise<Object>} Objeto con datos y código de estado
 * @returns {Promise<{data: Object|Array, statusCode: number}>} Estructura de respuesta:
 *   - data: Datos de búsqueda o array vacío en caso de error
 *   - statusCode: Código HTTP de respuesta (200, 401, 404, 500, etc.)
 * 
 * @throws {Object} Retorna objeto con statusCode 401 en caso de error de red
 * 
 * @example
 * // Búsqueda básica
 * const result = await getSearch('mexico', 0, 20, '', token);
 * if (result.statusCode === 200) {
 *   console.log('Resultados:', result.data.hits);
 *   console.log('Total:', result.data.total);
 * }
 * 
 * @example
 * // Búsqueda con filtro de categoría
 * const result = await getSearch('futbol', 0, 20, 'deportes', token);
 * 
 * @example
 * // Paginación (página 2)
 * const result = await getSearch('noticias', 1, 20, '', token);
 * 
 * @example
 * // Manejo completo de respuestas
 * const handleSearch = async () => {
 *   const result = await getSearch(query, page, 20, category, token);
 *   
 *   switch (result.statusCode) {
 *     case 200:
 *       setData(result.data);
 *       break;
 *     case 401:
 *       // Token expirado, renovar
 *       await refreshToken();
 *       break;
 *     case 500:
 *       setError('Error del servidor');
 *       break;
 *     default:
 *       setError('Error desconocido');
 *   }
 * };
 * 
 * @since 1.0.0
 * @author Valeria Garcia
 * 
 * @see {@link ../constantes/constantes.js} Para configuración de API_URL
 * @see {@link ./postToken.js} Para obtener token de autenticación
 */
const _getSearch = async (query, page, PAGESIZE, category, token) => {
  // Validación de parámetros
  if (!token) {
    return { statusCode: 401, data: [] };
  }

  // Validación de URL para prevenir SSRF
  const allowedDomains = ['unotv.com', 'clarosports.com', 'amxinfra.com'];
  const isValidURL = allowedDomains.some(domain => API_URL.includes(domain));
  
  if (!isValidURL) {
    console.error('URL no autorizada:', API_URL);
    return { statusCode: 403, data: [] };
  }

  // Construcción de URL con parámetros codificados
  const encodedQuery = encodeURIComponent(query || '');
  const offset = page * PAGESIZE;
  const apiURL = `${API_URL}search_content?q=${encodedQuery}&from=${offset}&size=${PAGESIZE}&category=${encodeURIComponent(category || '')}&utm_campaign=buscador`;

  const config = {
    method: 'GET',
    url: apiURL,
    timeout: 25000,
    headers: {
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  };

  try {
    const response = await axios(config);
    
    // Mapeo de códigos de respuesta
    const statusHandlers = {
      200: () => ({ data: response.data, statusCode: 200 }),
      401: () => ({ statusCode: 401, data: [] }), // Token inválido
      404: () => ({ statusCode: 404, data: [] }), // No encontrado
      500: () => ({ statusCode: 500, data: [] })  // Error servidor
    };

    const handler = statusHandlers[response.status];
    return handler ? handler() : { statusCode: response.status, data: [] };
    
  } catch (error) {
    // Manejo de errores de red/timeout
    if (error.response) {
      return { statusCode: error.response.status, data: [] };
    }
    
    // Error de red o timeout
    return { statusCode: 401, data: [] };
  }
};

export const getSearch = withTokenRetry(_getSearch);